import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import i18n, { getAppText } from 'src/i18n';
import { showMessage } from '../../components/components/SnackbarNotification/snackbarNotificationSlice';

export interface ManagerTypeDto {
    [key: string]: any;
    id: number;
    nameFr: string;
    nameEn: string;
    region_Id: number;
    teamKey: string;
}

export interface ManagerTypeState {
    managerTypes: ManagerTypeDto[];
    managerType: ManagerTypeDto;
    gettingManagerTypes: boolean;
    gettingManagerType: boolean;
    archivingManagerType: boolean;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error?: string;
}

const blankManagerTypeDto: ManagerTypeDto = {
    id: 0,
    nameFr: '',
    nameEn: '',
    region_Id: 0,
    teamKey: '',
};

const initialState: ManagerTypeState = {
    managerTypes: [],
    managerType: blankManagerTypeDto,
    gettingManagerTypes: false,
    gettingManagerType: false,
    archivingManagerType: false,
    status: 'idle',
    error: undefined,
};

export const managerTypeSlice = createSlice({
    name: 'managerTypes',
    initialState,
    reducers: {
        clearManagerTypes: (state) => {
            state.managerTypes = [];
            state.managerType = blankManagerTypeDto;
        },
        updateManagerType: (state, param) => {
            const { payload } = param;
            state.managerType = payload;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getManagerTypes.pending, (state, action) => {
                state.gettingManagerTypes = true;
                state.status = 'loading';
            })
            .addCase(getManagerTypes.fulfilled, (state, action) => {
                state.gettingManagerTypes = false;
                state.managerTypes = action.payload;
                state.status = 'succeeded';
            })
            .addCase(getManagerTypes.rejected, (state, action) => {
                state.gettingManagerTypes = false;
                state.managerTypes = [];
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(getManagerType.pending, (state, action) => {
                state.gettingManagerType = true;
                state.status = 'loading';
            })
            .addCase(getManagerType.fulfilled, (state, action) => {
                state.gettingManagerType = false;
                state.managerType = action.payload;
                state.status = 'succeeded';
            })
            .addCase(getManagerType.rejected, (state, action) => {
                state.gettingManagerType = false;
                state.managerType = blankManagerTypeDto;
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(archiveManagerType.pending, (state, action) => {
                state.archivingManagerType = true;
                state.status = 'loading';
            })
            .addCase(archiveManagerType.fulfilled, (state, action) => {
                state.archivingManagerType = false;
                state.managerTypes = state.managerTypes.filter((x) => x.id !== action.payload.id);
                state.status = 'succeeded';
            })
            .addCase(archiveManagerType.rejected, (state, action) => {
                state.archivingManagerType = false;
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(saveManagerType.pending, (state, action) => {
                state.status = 'loading';
                state.error = '';
            })
            .addCase(saveManagerType.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.error = '';
            })
            .addCase(saveManagerType.rejected, (state, action) => {
                state.status = 'failed';

                state.error = action.error.message;
            });
    },
});

export const { clearManagerTypes } = managerTypeSlice.actions;
export const { updateManagerType } = managerTypeSlice.actions;

export const getManagerTypes = createAsyncThunk('managerTypes/getManagerTypes', async (_, thunkAPI) => {
    const response = await axios.get(`/api/synergy/manager-types`);

    return response.data;
});

export const getManagerType = createAsyncThunk('managerTypes/getManagerType', async (id: number, thunkAPI) => {
    const response = await axios.get(`/api/synergy/manager-types/${id}`);

    return response.data;
});

export const archiveManagerType = createAsyncThunk('managerTypes/archiveManagerType', async (id: number, thunkAPI) => {
    const response = await axios.post(`/api/synergy/manager-types/archive/${id}`);
    thunkAPI.dispatch(
        showMessage({
            type: 'info',
            message: getAppText('archivedManagerType'),
        })
    );
    return response.data;
});

export const saveManagerType = createAsyncThunk('managerTypes/saveManagerType', async (managerTypeToSave: ManagerTypeDto, thunkAPI) => {
    if (managerTypeToSave.id === 0) {
        return axios
            .post(`/api/synergy/manager-types/create`, managerTypeToSave)
            .then((data) => {
                thunkAPI.dispatch(
                    showMessage({
                        type: 'info',
                        message: getAppText('createManagerType'),
                    })
                );
                return data;
            })
            .catch(async (error) => {
                const message = await error.response.data.text();
                thunkAPI.dispatch(
                    showMessage({
                        type: 'error',
                        message: i18n.t(message),
                    })
                );
                return error;
            });
    } else {
        const response = await axios.put(`/api/synergy/manager-types/put`, managerTypeToSave);
        thunkAPI.dispatch(
            showMessage({
                type: 'info',
                message: getAppText('saveManagerType'),
            })
        );
        return response.data;
    }
});
